import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@core-ui/Components/ui/dialog';
import { Button } from '@core-ui/Components/ui/button';
import { Input } from '@core-ui/Components/ui/input';
import { Badge } from '@core-ui/Components/ui/badge';
import { cn } from '@core-ui-lib/utils';

import { FileText, Loader2, UploadCloud } from 'lucide-react';

import useCsvValidator from '../hooks/useCsvValidator';
import CsvPreviewStats from './csv-preview/CsvPreviewStats';
import CsvPreviewTable from './csv-preview/CsvPreviewTable';
import Tip from '@ui/shared/Tip.tsx';

interface CreateListDialogProps {
  fileInputRef: React.RefObject<HTMLInputElement>;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  contactListTitle: string;
  onContactListTitleChange: (title: string) => void;
  csvFile: File | null;
  onCsvFileChange: (file: File | null) => void;
  isLoading: boolean;
  onSubmit: () => void;
  onDownloadTemplate: () => void;
}

interface FormErrors {
  name?: string;
  csv?: string;
  emptyCsv?: string;
}

type DialogPhase = 'form' | 'preview';

export default function CreateListDialog ({
  fileInputRef,
  isOpen,
  onOpenChange,
  contactListTitle,
  onContactListTitleChange,
  csvFile,
  onCsvFileChange,
  isLoading,
  onSubmit,
  onDownloadTemplate,
}: CreateListDialogProps): React.JSX.Element {
  const { t } = useTranslation();
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<{ name?: boolean; csv?: boolean }>({});
  const [isDragging, setIsDragging] = useState(false);
  const [dialogPhase, setDialogPhase] = useState<DialogPhase>('form');

  const validator = useCsvValidator();

  const createListSchema = z.object({
    name: z.string().min(1, t('contacts.createListDialog.errors.nameRequired')),
    csv: z.instanceof(File, { message: t('contacts.createListDialog.errors.csvRequired') }),
  });

  const validateField = useCallback(
    (field: 'name' | 'csv', value: string | File | null) => {
      if (field === 'name') {
        const result = z.string().trim().min(1, t('contacts.createListDialog.errors.nameRequired')).safeParse(value);
        setErrors((prev) => ({
          ...prev,
          name: result.success ? undefined : result.error.errors[0]?.message,
        }));
      } else if (field === 'csv') {
        const isValid = value instanceof File;
        setErrors((prev) => ({
          ...prev,
          csv: isValid ? undefined : t('contacts.createListDialog.errors.csvRequired'),
          emptyCsv: undefined,
        }));
      }
    },
    [t],
  );

  const validate = useCallback((): boolean => {
    const result = createListSchema.safeParse({ name: contactListTitle, csv: csvFile });
    if (!result.success) {
      const fieldErrors: FormErrors = {};
      result.error.errors.forEach((e) => {
        if (e.path[0] === 'name') {
          fieldErrors.name = e.message;
        }
        if (e.path[0] === 'csv') {
          fieldErrors.csv = e.message;
        }
      });
      setErrors(fieldErrors);
      setTouched({ name: true, csv: true });
      return false;
    }
    setErrors({});
    return true;
  }, [contactListTitle, csvFile, t]);

  const handleSubmit = (): void => {
    if (validate()) {
      onSubmit();
    }
  };

  const handleNameChange = (value: string): void => {
    onContactListTitleChange(value);
    if (touched.name === true) {
      validateField('name', value);
    }
  };

  const handleNameBlur = (): void => {
    setTouched((prev) => ({ ...prev, name: true }));
    validateField('name', contactListTitle);
  };

  const handleFileChange = (file: File | null): void => {
    onCsvFileChange(file);
    setTouched((prev) => ({ ...prev, csv: true }));
    validateField('csv', file);

    if (file !== null) {
      validator.validateFile(file);
      setDialogPhase('preview');
    }
  };

  const handleClearPreview = (): void => {
    setDialogPhase('form');
    validator.clearValidation();
    onCsvFileChange(null);
    setErrors((prev) => ({ ...prev, csv: undefined, emptyCsv: undefined }));
  };

  const handleOpenChange = (open: boolean): void => {
    if (!open) {
      setDialogPhase('form');
      validator.clearValidation();
      setErrors({});
      setTouched({});
    }
    onOpenChange(open);
  };

  const hasNoErrors = Object.values(errors).every(error => error === undefined);
  const canSubmit = hasNoErrors && contactListTitle.trim() !== '' && csvFile !== null && !isLoading;

  const isPreview = dialogPhase === 'preview' && validator.validationResult !== null;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className={cn(
          'text-muted-foreground border border-border transition-all',
          isPreview
            ? 'sm:max-w-225 max-h-[85vh] overflow-y-auto'
            : 'sm:max-w-130',
        )}
      >
        <DialogHeader>
          <DialogTitle data-qa-id="create-list-title" className="text-muted-foreground">{t('contacts.createListDialog.title')}</DialogTitle>
          {!isPreview && (
            <DialogDescription>
              {t('contacts.createListDialog.description')}
            </DialogDescription>
          )}
        </DialogHeader>

        {isPreview && validator.validationResult !== null ? (
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1 flex-1">
                <div className="text-xs font-medium text-muted-foreground">
                  {t('contacts.createListDialog.nameLabel')}
                </div>
                <Input
                  value={contactListTitle}
                  onChange={(event) => {
                    handleNameChange(event.target.value);
                  }}
                  onBlur={handleNameBlur}
                  aria-invalid={errors.name !== undefined}
                  placeholder={t('contacts.createListDialog.namePh')}
                  className={cn('text-muted-foreground',
                    touched.name === true && errors.name !== undefined &&
                      'border-error! ring-0! focus:ring-error! focus:border-error!',
                  )}
                />
                {touched.name === true && errors.name !== undefined && (
                  <p data-qa-id="list-name-error" className="text-xs text-error mt-1">{errors.name}</p>
                )}
              </div>
            </div>

            <CsvPreviewStats
              stats={validator.validationResult.stats}
              fileName={validator.fileName}
              fileSize={validator.fileSize}
              onRemoveFile={handleClearPreview}
              variant="full"
            />

            <CsvPreviewTable
              contacts={validator.validationResult.contacts}
              stats={validator.validationResult.stats}
              onRemoveContact={validator.removeContact}
              onUpload={handleSubmit}
              onCancel={handleClearPreview}
              isUploading={isLoading}
              variant="full"
              uploadableCount={validator.uploadableCount}
              canSubmit={contactListTitle.trim() !== '' && csvFile !== null && !isLoading}
            />
          </div>
        ) : (
          <>
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="text-sm font-medium text-muted-foreground">{t('contacts.createListDialog.nameLabel')}</div>
                <Input
                  data-qa-id="list-name"
                  value={contactListTitle}
                  onChange={(event) => {
                    handleNameChange(event.target.value);
                  }}
                  onBlur={handleNameBlur}
                  aria-invalid={errors.name !== undefined}
                  placeholder={t('contacts.createListDialog.namePh')}
                  className={cn('text-muted-foreground',
                    touched.name === true && errors.name !== undefined &&
                    'border-error! ring-0! focus:ring-error! focus:border-error!',
                  )}
                />
                {touched.name === true && errors.name !== undefined && (
                  <p data-qa-id="list-name-error" className="text-xs text-error mt-1">{errors.name}</p>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-medium text-muted-foreground">{t('contacts.createListDialog.csvLabel')}</div>
                  <Button
                    data-qa-id="download-template-button"
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="gap-2 text-muted-foreground"
                    onClick={onDownloadTemplate}
                    title={t('contacts.createListDialog.downloadTemplate')}
                  >
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    {t('contacts.createListDialog.downloadTemplate')}
                  </Button>
                </div>

                <label
                  className={cn(
                    'flex cursor-pointer items-center justify-between gap-3 rounded-md border border-dashed p-3',
                    'hover:bg-muted/40',
                    isDragging && 'bg-muted/50 border-primary',
                    touched.csv === true && errors.csv !== undefined && 'border-error!',
                  )}
                  onDragOver={(event) => {
                    event.preventDefault();
                    setIsDragging(true);
                  }}
                  onDragLeave={(event) => {
                    event.preventDefault();
                    setIsDragging(false);
                  }}
                  onDrop={(event) => {
                    event.preventDefault();
                    setIsDragging(false);

                    const selectedFile = event.dataTransfer.files?.[0] ?? null;
                    const isCsv = selectedFile?.name.toLowerCase().endsWith('.csv') || selectedFile?.type === 'text/csv';

                    if (!isCsv) {
                      handleFileChange(null);
                      return;
                    }

                    handleFileChange(selectedFile);
                  }}
                >
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <UploadCloud className="h-4 w-4" />
                    {csvFile !== null ? csvFile.name : t('contacts.createListDialog.dropzone')}
                  </div>
                  <input
                    data-qa-id="file-input"
                    type="file"
                    accept=".csv,text/csv"
                    style={{ display: 'none' }}
                    ref={fileInputRef}
                    className="hidden"
                    onChange={(event) => {
                      const selectedFile = event.target.files?.[0] ?? null;
                      handleFileChange(selectedFile);
                    }}
                  />
                </label>
                {touched.csv === true && errors.csv !== undefined && (
                  <p className="text-xs text-error mt-1">{errors.csv}</p>
                )}

                {touched.csv === true && errors.emptyCsv !== undefined && (
                  <p className="text-xs text-error mt-1">{errors.emptyCsv}</p>
                )}

                <Tip message="contacts.createListDialog.tip"/>

              </div>

              <div className="flex items-center gap-2">
                <div className="text-xs text-muted-foreground">{t('contacts.createListDialog.source')}</div>
                <Badge variant="secondary" className="rounded-full text-xs text-muted-foreground">
                  CSV
                </Badge>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button
                data-qa-id="list-cancel-button"
                className="bg-muted text-muted-foreground hover:bg-muted/80"
                onClick={() => {
                  handleOpenChange(false);
                }}
              >
                {t('contacts.createListDialog.cancel')}
              </Button>
              <Button
                data-qa-id="create-list-form-button"
                onClick={handleSubmit}
                disabled={!canSubmit}
                className="gap-2"
              >
                {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                {t('contacts.createListDialog.submit')}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}